// src/components/Remise.jsx
import React, { useMemo } from 'react';
import { toMoney } from '../utils/helpers';

function clampNumber(n, min, max, fallback = 0) {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  return Math.max(min, Math.min(max, x));
}

/**
 * calcTotalsWithRemise
 * - subtotal_before_remise : HT avant remise
 * - applique la remise sur HT (avant TVA)
 * - total_ht après remise
 * - total_tva calculé sur HT après remise
 * - total_ttc = total_ht + total_tva
 *
 * ✅ Compatible:
 * - accepte subtotalHT OU totalHT
 */
export function calcTotalsWithRemise({
  subtotalHT,
  totalHT,        // ✅ compat Sales.jsx
  tvaPct = 18,
  remisePct = 0,
  remiseAmount = 0,
}) {
  const baseHT = toMoney(
    subtotalHT != null ? subtotalHT : (totalHT != null ? totalHT : 0)
  );

  const tva = clampNumber(tvaPct, 0, 100, 18);
  const pct = clampNumber(remisePct, 0, 100, 0);
  const amt = toMoney(remiseAmount);

  // Priorité: si montant > 0 on le prend, sinon %.
  let effectiveRemise = 0;
  if (amt > 0) effectiveRemise = amt;
  else if (pct > 0) effectiveRemise = toMoney((baseHT * pct) / 100);

  // Remise ne peut pas dépasser HT
  effectiveRemise = clampNumber(effectiveRemise, 0, baseHT, 0);

  const htAfter = toMoney(baseHT - effectiveRemise);
  const tvaAmount = toMoney((htAfter * tva) / 100);
  const ttc = toMoney(htAfter + tvaAmount);

  return {
    subtotal_before_remise: baseHT,
    remise_pct: pct,
    remise_amount: effectiveRemise,
    total_ht: htAfter,
    total_tva: tvaAmount,
    total_ttc: ttc,
    // bonus utiles si tu veux afficher plus tard
    ht_net: htAfter,
    tva_amount: tvaAmount,
    ttc,
  };
}

/**
 * Remise UI
 * value = { remise_pct, remise_amount }
 */
export default function Remise({ value, onChange, subtotalHT = 0 }) {
  const remise_pct = clampNumber(value?.remise_pct, 0, 100, 0);
  const remise_amount = toMoney(value?.remise_amount);

  function patch(p) {
    onChange?.({ ...(value || {}), ...p });
  }

  const preview = useMemo(() => {
    return calcTotalsWithRemise({
      subtotalHT,
      tvaPct: 0, // preview remise sur HT uniquement (TVA gérée dans Sales)
      remisePct: remise_pct,
      remiseAmount: remise_amount,
    });
  }, [subtotalHT, remise_pct, remise_amount]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold">Remise</div>
          <div className="text-xs text-slate-500">
            Appliquée sur le HT (avant TVA). Tu peux saisir un % ou un montant.
          </div>
        </div>

        <button
          type="button"
          onClick={() => patch({ remise_pct: 0, remise_amount: 0 })}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
          title="Annuler la remise"
        >
          Annuler
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        <div>
          <label className="text-xs text-slate-500">Remise (%)</label>
          <input
            type="number"
            min={0}
            max={100}
            step="0.01"
            value={remise_pct}
            onChange={e =>
              patch({
                remise_pct: clampNumber(e.target.value, 0, 100, 0),
                // si tu touches le %, on reset le montant pour éviter double-remise
                remise_amount: 0,
              })
            }
            className="border border-slate-200 rounded-lg px-2 py-2 w-full focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
          />
        </div>

        <div>
          <label className="text-xs text-slate-500">Remise (montant)</label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={remise_amount}
            onChange={e =>
              patch({
                remise_amount: toMoney(e.target.value),
                // si tu touches le montant, on reset le %
                remise_pct: 0,
              })
            }
            className="border border-slate-200 rounded-lg px-2 py-2 w-full focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
          />
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
          <div className="text-xs text-slate-500">Aperçu remise sur HT</div>

          <div className="flex justify-between text-sm mt-1">
            <span>HT avant</span>
            <span className="font-medium">
              {preview.subtotal_before_remise.toLocaleString('fr-FR')} XOF
            </span>
          </div>

          <div className="flex justify-between text-sm">
            <span>Remise</span>
            <span className="font-medium">
              -{preview.remise_amount.toLocaleString('fr-FR')} XOF
            </span>
          </div>

          <div className="flex justify-between text-sm">
            <span>HT après</span>
            <span className="font-semibold">
              {preview.total_ht.toLocaleString('fr-FR')} XOF
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
